/**
 * VoiceComposerPanel.tsx (LK Staff — пarity с MIS)
 *
 * Голосовое составление плана в конструкторе ЛК.
 * Использует тот же action_key="constructor_voice_compose" в AMI.
 *
 * TODO-backend: LK backend нужны endpoints:
 *   POST /staff/ai-assist/voice/transcribe  (аналог MIS /ai-assist/voice/transcribe)
 *   POST /staff/ai-assist/proposals         (аналог MIS /ai-assist/proposals)
 *   POST /staff/ai-assist/proposals/:id/confirm (аналог MIS)
 * До реализации — panel показывает UI но API-вызовы вернут 404.
 *
 * Schema: ./schema/planVoiceOps.ts — ЕДИНЫЙ ИСТОЧНИК ТИПОВ для MIS и LK.
 */
import { useState } from 'react'
import type { AuthSession } from '../../../../types/plans'
import type { ConstructorPlan, ConstructorStage } from '../constructorTypes'
import type { PlanVoiceOp } from './schema/planVoiceOps'
import { parsePlanVoiceCommand, opToHuman } from './schema/planVoiceOps'

// ── Shared helpers ────────────────────────────────────────────────────────────

type RecordState = 'idle' | 'recording' | 'stopping'
type FlowStep = 'idle' | 'transcribing' | 'proposing' | 'preview' | 'applying' | 'done' | 'error'

const ACTION_KEY = 'constructor_voice_compose'
const UI_SURFACE = 'constructor_voice_panel'

function buildPlanContext(
  plan: ConstructorPlan,
  stages: ConstructorStage[],
): Record<string, unknown> {
  const stages_summary = stages
    .map((s, i) => `${i + 1}. «${s.name}» (${s.services?.length ?? 0} услуг)`)
    .join('; ')
  return {
    plan_id: plan.id,
    plan_title: `план #${plan.id}`,
    stages_summary,
  }
}

// ── Sub-components ────────────────────────────────────────────────────────────

function VoiceMicButton({
  onCaptured,
  disabled,
}: {
  onCaptured: (f: File) => void
  disabled?: boolean
}) {
  const [state, setState] = useState<RecordState>('idle')
  const recorderRef = { current: null as MediaRecorder | null }
  const chunksRef = { current: [] as Blob[] }

  const start = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
      const recorder = new MediaRecorder(stream)
      chunksRef.current = []
      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data)
      }
      recorder.onstop = () => {
        stream.getTracks().forEach((t) => t.stop())
        const blob = new Blob(chunksRef.current, { type: recorder.mimeType || 'audio/webm' })
        const ext = recorder.mimeType.includes('ogg') ? 'ogg' : 'webm'
        onCaptured(new File([blob], `voice.${ext}`, { type: blob.type }))
        setState('idle')
      }
      recorder.start()
      recorderRef.current = recorder
      setState('recording')
    } catch {
      setState('idle')
    }
  }

  const stop = () => {
    setState('stopping')
    recorderRef.current?.stop()
  }

  return (
    <button
      type="button"
      disabled={disabled || state === 'stopping'}
      onClick={state === 'recording' ? stop : start}
      className={`rounded-md px-3 py-1.5 text-sm font-medium ${
        state === 'recording'
          ? 'animate-pulse bg-red-50 text-red-600 ring-1 ring-red-300'
          : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
      }`}
    >
      {state === 'recording' ? '■ Стоп' : '🎙 Записать'}
    </button>
  )
}

function OpCard({ op, onRemove }: { op: PlanVoiceOp; onRemove: () => void }) {
  return (
    <div className="flex items-start gap-2 rounded-lg border border-slate-200 bg-white px-2.5 py-2">
      <span className="mt-0.5 shrink-0 rounded bg-slate-100 px-1 py-0.5 text-[10px] font-semibold uppercase text-slate-500">
        {op.op.replace('_', ' ')}
      </span>
      <span className="min-w-0 flex-1 text-xs leading-snug text-slate-700">{opToHuman(op)}</span>
      <button type="button" className="text-xs text-slate-400 hover:text-slate-600" onClick={onRemove}>
        ×
      </button>
    </div>
  )
}

// ── Main component ────────────────────────────────────────────────────────────

export function VoiceComposerPanel({
  plan,
  stages,
  session,
  apiBase,
  onApplied,
}: {
  plan: ConstructorPlan
  stages: ConstructorStage[]
  session: AuthSession
  apiBase: string
  onApplied: () => void
}) {
  const [step, setStep] = useState<FlowStep>('idle')
  const [message, setMessage] = useState('')
  const [manualText, setManualText] = useState('')
  const [showManual, setShowManual] = useState(false)
  const [proposalId, setProposalId] = useState<string | null>(null)
  const [ops, setOps] = useState<PlanVoiceOp[]>([])

  const planContext = buildPlanContext(plan, stages)
  // apiBase passed as prop (bootstrap.apiBase from ConstructorRightDrawer)

  const transcribe = async (file: File) => {
    setStep('transcribing')
    try {
      const fd = new FormData()
      fd.set('action_key', ACTION_KEY)
      fd.set('ui_surface', UI_SURFACE)
      fd.set('audio', file)
      const res = await fetch(`${apiBase}/staff/ai-assist/voice/transcribe`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${(session as unknown as Record<string, unknown>).accessToken}` },
        body: fd,
      })
      if (!res.ok) throw new Error(`${res.status}`)
      const data = await res.json() as { transcription: { text: string } }
      const text = data.transcription.text.trim()
      setManualText(text)
      await propose(text)
    } catch (e) {
      setStep('error')
      setMessage(String(e))
    }
  }

  const propose = async (inputText: string) => {
    setStep('proposing')
    try {
      const res = await fetch(`${apiBase}/staff/ai-assist/proposals`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${(session as unknown as Record<string, unknown>).accessToken}`,
        },
        body: JSON.stringify({
          action_key: ACTION_KEY,
          ui_surface: UI_SURFACE,
          source_kind: 'voice',
          input_text: inputText,
          target_entity_id: String(plan.id),
          metadata: { plan_context: planContext },
        }),
      })
      if (!res.ok) throw new Error(`${res.status}`)
      const data = await res.json() as { proposal: { proposal_id: string; proposed_payload: unknown } }
      const parseResult = parsePlanVoiceCommand(data.proposal.proposed_payload)
      if (!parseResult.command) throw new Error('AI не смог распознать команду — попробуйте ещё раз')
      setProposalId(data.proposal.proposal_id)
      setOps(parseResult.command.operations)
      if (parseResult.skipped.length > 0) {
        setMessage(`Часть команды не распознана (${parseResult.skipped.length} оп.). Остальное готово к применению.`)
      }
      setStep('preview')
    } catch (e) {
      setStep('error')
      setMessage(String(e))
    }
  }

  const confirm = async () => {
    if (!proposalId) return
    setStep('applying')
    try {
      const res = await fetch(`${apiBase}/staff/ai-assist/proposals/${proposalId}/confirm`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${(session as unknown as Record<string, unknown>).accessToken}`,
        },
        body: JSON.stringify({ apply_immediately: true, note: `ops: ${ops.map((o) => o.op).join(', ')}` }),
      })
      if (!res.ok) throw new Error(`${res.status}`)
      setStep('done')
      onApplied()
      setTimeout(() => setStep('idle'), 2000)
    } catch (e) {
      setStep('error')
      setMessage(String(e))
    }
  }

  const reset = () => {
    setStep('idle')
    setMessage('')
    setOps([])
    setProposalId(null)
    setManualText('')
  }

  return (
    <div className="space-y-3">
      <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Голосовое составление</p>
      <p className="text-xs text-slate-500">
        Запишите команду: этапы, услуги, рассрочку, шеринг, таймер.
      </p>

      {step === 'idle' && (
        <div className="space-y-2">
          <VoiceMicButton onCaptured={transcribe} />
          <button
            type="button"
            className="text-xs text-slate-500 underline"
            onClick={() => setShowManual((v) => !v)}
          >
            {showManual ? 'Скрыть текст' : 'Ввести текстом'}
          </button>
          {showManual && (
            <div className="space-y-1.5">
              <textarea
                value={manualText}
                onChange={(e) => setManualText(e.target.value)}
                rows={3}
                placeholder="Пример: все пломбы из плана 12.05 в первый этап «Терапевтический», рассрочка 12 мес с 30 мая"
                className="w-full rounded-md border border-slate-200 p-2 text-xs"
              />
              <button
                type="button"
                disabled={!manualText.trim()}
                onClick={() => propose(manualText)}
                className="rounded-md bg-blue-600 px-3 py-1.5 text-xs font-medium text-white disabled:opacity-50"
              >
                Разобрать
              </button>
            </div>
          )}
        </div>
      )}

      {step === 'transcribing' && (
        <p className="text-xs text-slate-500">Распознаём речь…</p>
      )}
      {step === 'proposing' && (
        <p className="text-xs text-slate-500">AI разбирает команду…</p>
      )}
      {step === 'applying' && (
        <p className="text-xs text-slate-500">Применяем операции…</p>
      )}
      {step === 'done' && (
        <p className="text-xs font-medium text-emerald-600">Готово! План обновлён.</p>
      )}
      {step === 'error' && (
        <div className="space-y-1.5">
          <p className="text-xs text-red-600">{message}</p>
          <button type="button" className="text-xs text-slate-500 underline" onClick={reset}>
            Попробовать снова
          </button>
        </div>
      )}

      {step === 'preview' && ops.length > 0 && (
        <div className="space-y-2">
          <div className="space-y-1.5">
            {ops.map((op, i) => (
              <OpCard key={i} op={op} onRemove={() => setOps((prev) => prev.filter((_, idx) => idx !== i))} />
            ))}
          </div>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={confirm}
              className="rounded-md bg-blue-600 px-3 py-1.5 text-xs font-medium text-white"
            >
              Применить ({ops.length})
            </button>
            <button type="button" onClick={reset} className="text-xs text-slate-500 underline">
              Отмена
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
